<template>
    <!-- MY1-3084: centred title section, aligned with the My Spending
         transaction sidebar — icon, title, card, then the hidden pill and the
         auto-tracking label. The action menu lives in the panel header. -->
    <header
        class="-mx-6 mb-6 flex flex-col items-center px-6 pb-8 pt-6 text-center bg-gradient-to-b from-slate-50 to-white dark:from-slate-800/60 dark:to-transparent"
        data-test="benefit-details-header"
    >
        <div
            class="flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200/70 dark:bg-slate-800 dark:ring-slate-700"
        >
            <BenefitIcon
                :category="benefit?.benefit?.category"
                :title="benefit?.benefit?.benefit_title"
                :description="benefit?.benefit?.benefit_description"
                svgClass="w-8 h-8 text-slate-600 dark:text-slate-300"
            />
        </div>

        <h3
            class="mt-3 max-w-full break-words text-2xl font-medium leading-8 text-slate-800 dark:text-slate-100"
            data-test="benefit-title"
        >
            {{ benefit?.benefit?.benefit_title || 'Benefit Details' }}
        </h3>

        <div
            class="mt-2 flex max-w-full min-w-0 items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400"
            data-test="benefit-card"
        >
            <ImageLoader class="shrink-0">
                <template #image>
                    <img
                        :src="`${cdnUrl}/${benefit?.card?.image?.path}/_small/${benefit?.card?.image?.file_name}`"
                        :alt="benefit?.card?.title"
                        class="h-4 w-6 rounded-sm"
                    />
                </template>
                <template #error>
                    <div
                        class="flex h-4 w-6 items-center justify-center rounded-sm border border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-700"
                    >
                        <Icon
                            icon="credit-card"
                            class="h-3 w-3 text-slate-400"
                        />
                    </div>
                </template>
            </ImageLoader>
            <span class="truncate">
                {{
                    benefit?.card?.title || benefit?.card_name || 'Unknown Card'
                }}
                <span
                    v-if="showLastFour && benefit?.connected_account_last_four"
                    class="text-xs text-slate-500 dark:text-slate-400"
                >
                    ({{ benefit?.connected_account_last_four }})
                </span>
                <span v-if="showOwner && benefit?.user?.name">
                    · {{ benefit.user.name }}
                </span>
            </span>
        </div>

        <!-- Hidden state, shown the way My Spending shows a hidden transaction -->
        <span
            v-if="isHidden"
            v-tooltip="'Benefit hidden from list'"
            class="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-sm font-medium text-slate-500 dark:bg-slate-700 dark:text-slate-400"
            data-test="hidden-badge"
        >
            <Icon icon="eye-off" :width="14" :height="14" />
            Hidden
        </span>

        <BenefitAutoTrackingLabel
            :auto-tracking="benefit?.auto_tracking"
            :can-enable-spending="canEnableSpending"
            class="mt-2"
        />
    </header>
</template>

<script setup>
import BenefitIcon from '@/components/card-benefits/benefit-icon.vue';
import BenefitAutoTrackingLabel from '@/components/card-benefits/benefit-auto-tracking-label.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';

defineProps({
    // The selected benefit row from the benefits store.
    benefit: {
        type: Object,
        default: null,
    },
    isHidden: {
        type: Boolean,
        default: false,
    },
    showLastFour: {
        type: Boolean,
        default: false,
    },
    showOwner: {
        type: Boolean,
        default: false,
    },
    cdnUrl: {
        type: String,
        default: '',
    },
    // MY1-3083: the viewer owns the card, so the spending_disabled label may
    // turn My Spending on in place.
    canEnableSpending: {
        type: Boolean,
        default: false,
    },
});
</script>
